import type { PutObjectCommandInput } from "@aws-sdk/client-s3";
import { S3Client } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import isFunction from "lodash/isFunction";
import isString from "lodash/isString";
import isUndefined from "lodash/isUndefined";
import * as Sentry from "@sentry/browser";
import Constants from "const/constants";
import { publicRuntimeConfig } from "util/env-utils";

/**
 * @function
 * @summary - Get the S3 path for temporary uploads, optionally with a URI encoded suffix
 * @param suffix - The suffix of the upload path (no leading slash)
 *
 * @returns The S3 path
 */
export function getTempUploadPath(suffix?: string) {
    let path = `uploads/tmp/or/${App.Models.user.get(`organizationId`)}/`;

    if (isString(suffix)) {
        path += encodeURIComponent(suffix);
    }

    return path;
}

/**
 * @function
 * @returns The maximum size of an upload in bytes
 */
export function getUploadMaxSize() {
    return 5 * 1024 * 1024 * 1024; // 5GB
}

export interface S3UploadOptions {
    file?: File | string;
    onSuccess?: (result: S3UploadResponse) => void;
    onProgress?: (progress: number) => void;
    additionalData?: unknown; // Additional data to be passed in the response, from the caller
    isBase64?: boolean;
}

export type S3UploadResponse = ReturnType<typeof buildResponse>;

const MULTIPART_UPLOAD_MAX_PARTS = 10_000;
const MULTIPART_UPLOAD_PART_SIZE_MIN = 1024 ** 2 * 5;
/*
 * According to the docs https://docs.aws.amazon.com/AmazonS3/latest/userguide/qfacts.html#qfacts
 * The maximum number of upload parts is 10_000; the max upload size is 5TiB
 * It stands to reason that the partsize need not be larger than 5TiB/10_000 which is about 524MiB
 */
const MULTIPART_UPLOAD_PART_SIZE_MAX = 1024 ** 2 * 512;

// This is based on the AWS-SDK v2 implementation of managed upload.
function findOptimalPartSize({ size }: File): number {
    if (!size) {
        return MULTIPART_UPLOAD_PART_SIZE_MIN;
    }

    const calculatedPartSize = Math.ceil(size / MULTIPART_UPLOAD_MAX_PARTS);

    return Math.max(
        MULTIPART_UPLOAD_PART_SIZE_MIN,
        Math.min(calculatedPartSize, MULTIPART_UPLOAD_PART_SIZE_MAX)
    );
}

export async function s3upload(options: S3UploadOptions = {}) {
    if (isUndefined(options.file)) {
        throw `S3Utils requires \`upload\` to have a \`file\``;
    }

    const file = options.file as File; // actually this can also be a string
    const s3Prefix = createS3Prefix();
    const key = getTempUploadPath() + s3Prefix + file.name;
    const params: PutObjectCommandInput = {
        Key: key,
        ContentType: file.type,
        Body: file,
        ACL: `public-read`,
        Bucket: publicRuntimeConfig.s3BucketName
    };

    const s3 = new S3Client({
        useAccelerateEndpoint: true,
        // correctClockSkew: true, // TODO: Investigate further if this has an equivalent
        /* // TODO: Investigate further if this has an equivalent
		httpOptions: {
            timeout: 4 * 60 * 1000
        }, */
        credentials: {
            accessKeyId: publicRuntimeConfig.s3AccessKey,
            secretAccessKey: publicRuntimeConfig.s3Secret
        },
        region: publicRuntimeConfig.AWS_REGION
    });

    const upload = new Upload({
        client: s3,
        params,
        partSize: findOptimalPartSize(file)
    });

    if (isFunction(options.onProgress)) {
        upload.on(`httpUploadProgress`, ({ loaded, total }) =>
            options.onProgress((loaded / total) * 100)
        );
    }

    try {
        await upload.done();
    } catch (cause) {
        Sentry.captureException(cause);
        throw new Error("There was an error uploading the file", { cause });
    }

    const response = buildResponse({
        file,
        key,
        s3Prefix,
        additionalData: options.additionalData
    });

    if (isFunction(options.onSuccess)) {
        options.onSuccess(response);
    }

    return {
        response,
        upload
    };
}

export const upload = async (options: S3UploadOptions) => {
    const { response } = await s3upload(options);
    return response;
};

/**
 * @function
 * @summary - Create a File object from a data URL
 *
 * @param dataurl
 *
 */
export function dataURLtoFile(dataurl: string) {
    const mime = extractMimeFromBase64(dataurl);
    const fileExtension = mime === `image/png` ? `temp.png` : `temp.jpg`;
    const arr = dataurl.split(`,`);
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileExtension, { type: mime });
}

/**
 * @function
 * @summary - Upload base64 data to S3 as a file
 */
export async function uploadbase64(options: S3UploadOptions = {}) {
    if (isUndefined(options.file)) {
        throw `S3Utils requires \`uploadbase64\` to have an \`img\``;
    }

    const file = dataURLtoFile(options.file as string);
    const uploadOptions: S3UploadOptions = Object.assign({}, options, {
        ContentEncoding: `base64`,
        file
    });

    return upload(uploadOptions);
}

/**
 * @function
 * @summary - Returns the mime from a base64 file
 * @param base64String - file as a base 64 String
 *
 */
export function extractMimeFromBase64(base64String) {
    const matches = base64String.match(
        /data:(?<mimetype>(?:\w|[+-])+\/(?:\w|[+-])+);/
    );

    return matches?.groups?.mimetype;
}

/**
 * @function
 * @summary - Upload multiple base64 data objects concurrently
 * @param uploads - Any number of objects to be passed to `s3-utils/uploadbase64()` (or an array of such objects)
 *
 */
export async function concurrentUpload(uploads: S3UploadOptions[]) {
    return Promise.all(
        uploads.map((file) =>
            file.isBase64 ? uploadbase64(file) : upload(file)
        )
    );
}

/**
 * @function
 * @summary - Get the pre-signed URL for a download.
 * 			  Note: removes from the filename any reserved characters for the Windows OS and removes comma (,) as it is not supported
 * 			  in the content-disposition header in the Google Chrome browser.
 *
 * @param id - The grab ID
 * @param cloudfrontUrl - The url of the file
 * @param filename - The filename used in the content-disposition header for the signed url
 *
 */
export function getDownloadPreSignedUrl(
    id: string,
    cloudfrontUrl = ``,
    filename?: string
) {
    const cdnUrl = Constants.CDN_URL;
    const s3ObjectKey = cloudfrontUrl?.replace(`${cdnUrl}/`, ``);
    const params: {
        key: string;
        filename?: string;
    } = {
        key: s3ObjectKey
    };

    let headerFilename =
        filename && filename?.replace(/([,<>:\"\\/|?*])/gm, ` `);
    if (headerFilename) {
        if (headerFilename.charAt(0) === `.`) {
            headerFilename = ` ${headerFilename}`;
        }
        params.filename = headerFilename;
    }

    return `ws/grabs/${id}/downloadUrl?${$.param(params)}`;
}

/**
 * @function
 * @summary - Build an s3 response object
 */
const buildResponse = ({
    file,
    key,
    s3Prefix,
    additionalData
}: {
    file: File | string;
    key: string;
    s3Prefix: string;
    additionalData?: any;
}) => {
    const isFileString = typeof file === "string";
    return {
        filename: !isFileString ? file.name : undefined,
        s3Filename: !isFileString ? s3Prefix + file.name : undefined,
        key,
        url: isFileString ? file : getFileUrl(key),
        progress: 100,
        isWriteable: false,
        mimetype: !isFileString ? file.type : undefined,
        additionalData
    };
};

/**
 * @function
 * @summary - Get the URL of a file on the CDN
 * @param key The filename
 *
 * @returns The URL of the file on the CDN
 */
export const getFileUrl = function (key: string): string {
    return `${Constants.CDN_URL}/${key}`;
};

/**
 * @function
 * @summary - Generate a prefix for s3 uploads
 *
 * @returns An s3 prefix
 */
export const createS3Prefix = function () {
    return `${Math.random().toString(36).substring(7)}_`;
};
